<template>
  <div
    v-if="isEligible && wantsAnotherRide !== false"
    class="next-ride-check"
  >
    <template v-if="wantsAnotherRide === null">
      <div class="row">
        <p class="col-12 text-center mb-2">
          Will you need another ride today?
        </p>
      </div>
      <div class="row justify-content-center ask-permission-buttons">
        <p-button
          type="purple"
          round
          @click="wantsAnotherRide = true"
        >
          Yes
        </p-button>
        <p-button
          type="warning"
          round
          @click="wantsAnotherRide = false"
        >
          No
        </p-button>
      </div>
    </template>

    <template v-else>
      <template v-if="isAskPermissionShown">
        <div class="row">
          <p class="col-12 text-center mb-2">
            Would you like us to text you with instructions to order your next ride?
          </p>
        </div>
        <div class="row justify-content-center ask-permission-buttons">
          <p-button
            :disabled="isPersisting"
            type="purple"
            round
            @click="accept"
          >
            Yes
          </p-button>
          <p-button
            :disabled="isPersisting"
            type="warning"
            round
            @click="decline"
          >
            No
          </p-button>
        </div>
        <p
          v-if="persistError"
          class="col-12 text-center text-red mt-2"
        >
          Failed to save the answer. {{ persistError }}
        </p>
      </template>

      <p
        v-else
        class="col-12 text-center"
      >
        {{ script }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { updateCaller } from '@/services/caller/updateCaller';
import util from '@/Utils/util';

interface NextRideCheckCaller {
  id: string;
  isOnPartnership?: boolean | null;
  transactionalSms?: boolean;
  nextRideSmsOrdering?: boolean;
  gogoNumber?: string;
}

const props = defineProps<{
  caller: NextRideCheckCaller | null;
  /** lifetime completed rides count; null/undefined = unknown -> section hidden */
  totalRides?: number | null;
  /** one-way intent (round trips are out of scope); parent computes */
  isOneWay: boolean;
  /** true while a driver is being matched (set before the order POST) */
  matching: boolean;
}>();

// null = not answered yet; true = Yes; false = No
const answered = ref<boolean | null>(null);
// null = not asked yet; true = Yes; false = No
const wantsAnotherRide = ref<boolean | null>(null);
const isPersisting = ref(false);
const persistError = ref('');

const resetAnswerState = () => {
  answered.value = null;
  wantsAnotherRide.value = null;
  isPersisting.value = false;
  persistError.value = '';
};

watch(
  () => props.matching,
  (matching) => {
    if (!matching) {
      resetAnswerState();
    }
  },
);

watch(
  () => props.caller?.id,
  () => {
    resetAnswerState();
  },
);

const isSmsAllowed = computed(() => {
  const caller = props.caller;
  if (!caller) {
    return false;
  }
  // default-ON semantics: absent permission never disables the promise
  const permissionOn = caller.nextRideSmsOrdering !== false;
  const masterOn = caller.transactionalSms !== false;
  return permissionOn && masterOn;
});

const isEligible = computed(() => {
  if (!props.matching || !props.isOneWay || !props.caller) {
    return false;
  }
  if (props.caller.isOnPartnership) {
    return false;
  }
  const totalRides = props.totalRides;
  if (totalRides === null || totalRides === undefined) {
    return false; // unknown count: hide the section rather than guess
  }
  return totalRides < 4;
});

const isAskPermissionShown = computed(() => isEligible.value && isSmsAllowed.value && answered.value === null);

const script = computed(() => {
  if (!props.caller) {
    return '';
  }
  const number = props.caller.gogoNumber;
  if (isSmsAllowed.value && answered.value !== false) {
    return `We'll text you when you've been dropped off with instructions for ordering your next ride. You can text us back or call us when you're ready to be picked up. The number is ${number}.`;
  }
  return `When you're ready for your next ride, call us and we'll send a new driver. Do you have the number? ${number}.`;
});

const accept = () => {
  answered.value = true;
};

const decline = async () => {
  // Capture the exact caller object: a replacement mid-flight (e.g. refetch) must not let this
  // mutation's stale result clobber the fresh object's state.
  const targetCaller = props.caller;
  if (!targetCaller) {
    return;
  }
  isPersisting.value = true;
  persistError.value = '';
  try {
    await updateCaller({
      callerId: targetCaller.id,
      data: { nextRideSmsOrdering: false },
    });
    if (props.caller === targetCaller) {
      targetCaller.nextRideSmsOrdering = false;
      answered.value = false;
    }
  } catch (err: unknown) {
    if (props.caller === targetCaller) {
      persistError.value = util.getGraphqlError(err, false) || (err instanceof Error ? err.message : String(err));
    }
  } finally {
    isPersisting.value = false;
  }
};
</script>

<style scoped>
.next-ride-check {
  border: 1px dashed #b8c6da;
  border-radius: 6px;
  padding: 12px 8px;
  margin-bottom: 12px;
  background-color: #f9fbfd;
}

.ask-permission-buttons {
  column-gap: 16px;
}
</style>
